import React, { useEffect, useState } from 'react'
import { addDoc, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'

const CATEGORIES = ['Engineering', 'Execution', 'Project Quality', 'Safety']
const WORK_AREAS = ['Substation', 'BESS', 'Collection', 'TLine']
const SEVERITIES = ['Low', 'Medium', 'High', 'Critical']

interface Props {
  onClose: () => void
  onCreated: () => void
}

export default function LessonModal({ onClose, onCreated }: Props) {
  const { orgId, userData } = useAuth()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState(CATEGORIES[0])
  const [workArea, setWorkArea] = useState(WORK_AREAS[0])
  const [severity, setSeverity] = useState('Low')
  const [projectId, setProjectId] = useState('')
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!orgId) return
    const q = query(
      collection(db, 'projects'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    )
    const unsub = onSnapshot(q, snap => {
      setProjects(snap.docs.map(d => ({ id: d.id, name: d.data().name || 'Unknown' })))
    })
    return () => unsub()
  }, [orgId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('LessonModal submit', { orgId, title, description, category, workArea, severity, projectId })
    if (!orgId) {
      setError('orgId is missing — please log out and back in')
      return
    }
    setSaving(true)
    setError('')
    try {
      await addDoc(collection(db, 'organizations', orgId, 'lessons'), {
        orgId,
        title,
        description,
        category,
        workArea,
        severity,
        projectId,
        status: 'Open',
        createdBy: userData?.name || userData?.email || 'unknown',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      onCreated()
      onClose()
    } catch (err: any) {
      console.error('LessonModal error', err)
      setError(err?.message || 'Failed to create lesson. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New Lesson</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
              placeholder="Brief lesson title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
              rows={3}
              placeholder="What happened, what was learned"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Area</label>
              <select value={workArea} onChange={e => setWorkArea(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {WORK_AREAS.map(w => <option key={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {SEVERITIES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                <option value="">Select project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-accent text-white py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:bg-amber-300">
              {saving ? 'Saving...' : 'Create Lesson'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
